import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Shield } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PasscodeVerification = ({ isOpen, onClose, onVerified, title = "Verify Passcode", message }) => {
  const [passcode, setPasscode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (passcode.length !== 6) {
      toast.error('Please enter a 6-digit passcode');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/verify-passcode`, 
        { passcode },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      toast.success('Passcode verified');
      setPasscode('');
      onVerified();
    } catch (error) {
      console.error('Passcode verification failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid passcode');
      setPasscode('');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPasscode('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#1A4D2E]/10 rounded-lg">
              <Shield className="text-[#1A4D2E]" size={20} />
            </div>
            <DialogTitle>{title}</DialogTitle>
          </div>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <p className="text-sm text-stone-600">
            {message || 'This action requires additional verification. Please enter your 6-digit passcode.'}
          </p>
          
          <div>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={passcode}
              onChange={(e) => setPasscode(e.target.value.replace(/\D/g, ''))}
              placeholder="••••••"
              className="text-2xl tracking-widest text-center font-mono"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && passcode.length === 6) {
                  handleVerify();
                }
              }}
            />
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleVerify} 
            disabled={passcode.length !== 6 || loading}
            className="bg-[#1A4D2E]"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PasscodeVerification;
